import { authFetch } from "@/lib/api-client";
import type {
  Persona,
  PersonaListResponse,
  PersonaOnboardingAcceptRequest,
  PersonaOnboardingActionResult,
  PersonaOnboardingCompleteRequest,
  PersonaOnboardingCompleteResult,
  PersonaOnboardingContinueRequest,
  PersonaOnboardingContinueResult,
  PersonaOnboardingRejectRequest,
  PersonaOnboardingStatus,
  PersonaOnboardingSuggestionsResponse,
  PersonaOnboardingUndoRequest,
  PersonaUsage,
} from "../types/personas.types";

export async function fetchPersonas(options?: {
  includeRejected?: boolean;
}): Promise<PersonaListResponse> {
  const includeRejected = options?.includeRejected === true;
  const path = includeRejected ? "/personas/list?includeRejected=true" : "/personas/list";
  const { data } = await authFetch<PersonaListResponse>(path);
  return data;
}

export async function fetchPersona(id: string): Promise<Persona> {
  const { data } = await authFetch<Persona>(`/personas/${id}`);
  return data;
}

export async function fetchPersonaUsage(id: string): Promise<PersonaUsage> {
  const { data } = await authFetch<PersonaUsage>(`/personas/${id}/usage`);
  return data;
}

export interface DiscoverPersonasParams {
  tenantId: string;
  batchSize?: number;
}

/** POSTs `/personas/discover` and returns the correlationId for `useAmberMutation` to poll. */
export async function discoverPersonas(
  params: DiscoverPersonasParams,
): Promise<{ correlationId: string }> {
  const idempotencyKey = crypto.randomUUID();
  const { correlationId } = await authFetch<void>("/personas/discover", {
    method: "POST",
    body: JSON.stringify({ ...params, idempotencyKey }),
  });
  if (!correlationId) {
    throw new Error("discoverPersonas: API response missing correlationId — cannot poll discover status");
  }
  return { correlationId };
}

export async function fetchPersonaOnboardingStatus(): Promise<PersonaOnboardingStatus> {
  const { data } = await authFetch<PersonaOnboardingStatus>("/personas/onboarding/status");
  return data;
}

export async function fetchPersonaOnboardingSuggestions(): Promise<PersonaOnboardingSuggestionsResponse> {
  const { data } = await authFetch<PersonaOnboardingSuggestionsResponse>(
    "/personas/onboarding/suggestions",
  );
  return data;
}

export async function acceptPersonaOnboardingSuggestion(
  body: PersonaOnboardingAcceptRequest,
): Promise<PersonaOnboardingActionResult> {
  const { data } = await authFetch<PersonaOnboardingActionResult>("/personas/onboarding/accept", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data;
}

export async function rejectPersonaOnboardingSuggestion(
  body: PersonaOnboardingRejectRequest,
): Promise<PersonaOnboardingActionResult> {
  const { data } = await authFetch<PersonaOnboardingActionResult>(
    "/personas/onboarding/reject",
    { method: "POST", body: JSON.stringify(body) },
  );
  return data;
}

export async function undoPersonaOnboardingSuggestion(
  body: PersonaOnboardingUndoRequest,
): Promise<PersonaOnboardingActionResult> {
  const { data } = await authFetch<PersonaOnboardingActionResult>("/personas/onboarding/undo", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data;
}

export async function continuePersonaOnboarding(
  body: PersonaOnboardingContinueRequest = {},
): Promise<PersonaOnboardingContinueResult> {
  const { data } = await authFetch<PersonaOnboardingContinueResult>("/personas/onboarding/continue", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data;
}

export async function completePersonaOnboarding(
  body: PersonaOnboardingCompleteRequest = {},
): Promise<PersonaOnboardingCompleteResult> {
  const { data } = await authFetch<PersonaOnboardingCompleteResult>("/personas/onboarding/complete", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return data;
}
